import { Currency, ETHER, JSBI, TokenAmount } from '@uniswap/sdk'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Plus } from 'react-feather'
import { Text } from 'rebass'
import { useTranslation } from 'react-i18next'
import { ButtonDropdownLightNew } from '../../components/Button'
import { LightCardNew } from '../../components/Card'
import { AutoColumn, ColumnCenter } from '../../components/Column'
import CurrencyLogo from '../../components/CurrencyLogo'
import { FindPoolTabs } from '../../components/NavigationTabs'
import { MinimalPositionCard } from '../../components/PositionCard'
import Row from '../../components/Row'
import CurrencySearchModal from '../../components/SearchModal/CurrencySearchModal'
import { PairState, usePair } from '../../data/Reserves'
import { useActiveWeb3React } from '../../hooks'
import { usePairAdder } from '../../state/user/hooks'
import { useTokenBalance } from '../../state/wallet/hooks'
import { StyledInternalLink, StyledInternalNewLink } from '../../theme'
import { currencyId } from '../../utils/currencyId'
import AppBody from '../AppBody'
import { Dots, PlusIcon } from '../Pool/styleds'
import { BlueCard } from '../../components/Card'
import { TYPE } from '../../theme'
import { ThemeContext } from 'styled-components'

enum Fields {
  TOKEN0 = 0,
  TOKEN1 = 1
}

export default function PoolFinder() {
  const { account } = useActiveWeb3React()

  const [showSearch, setShowSearch] = useState<boolean>(false)
  const [activeField, setActiveField] = useState<number>(Fields.TOKEN1)
  const themes = useContext(ThemeContext)
  const [currency0, setCurrency0] = useState<Currency | null>(ETHER)
  const [currency1, setCurrency1] = useState<Currency | null>(null)

  const [pairState, pair] = usePair(currency0 ?? undefined, currency1 ?? undefined)
  const addPair = usePairAdder()
  useEffect(() => {
    if (pair) {
      addPair(pair)
    }
  }, [pair, addPair])

  const { t } = useTranslation()

  const validPairNoLiquidity: boolean =
    pairState === PairState.NOT_EXISTS ||
    Boolean(
      pairState === PairState.EXISTS &&
      pair &&
      JSBI.equal(pair.reserve0.raw, JSBI.BigInt(0)) &&
      JSBI.equal(pair.reserve1.raw, JSBI.BigInt(0))
    )

  const position: TokenAmount | undefined = useTokenBalance(account ?? undefined, pair?.liquidityToken)
  const hasPosition = Boolean(position && JSBI.greaterThan(position.raw, JSBI.BigInt(0)))

  const handleCurrencySelect = useCallback(
    (currency: Currency) => {
      if (activeField === Fields.TOKEN0) {
        setCurrency0(currency)
      } else {
        setCurrency1(currency)
      }
    },
    [activeField]
  )

  const handleSearchDismiss = useCallback(() => {
    setShowSearch(false)
  }, [setShowSearch])

  const prerequisiteMessage = (
    <LightCardNew padding="45px 10px">
      <Text textAlign="center">
        {!account ? 'Connect to a wallet to find pools' : 'Select a token to find your liquidity.'}
      </Text>
    </LightCardNew>
  )

  return (<>
    <AppBody style={{ boxShadow: '0px 2px 0px 1px rgba(14, 37, 74, 0.1)' }}>
      <FindPoolTabs />
      <AutoColumn style={{ padding: '1rem' }} gap="md">
        <BlueCard>
          <AutoColumn gap="10px">
            <TYPE.link fontWeight={500} fontSize={16} color={themes.text7} style={{ textAlign: 'justify' }}>
              {t('Tip_Use_this')}.
            </TYPE.link>
          </AutoColumn>
        </BlueCard>
        <ButtonDropdownLightNew
          onClick={() => {
            setShowSearch(true)
            setActiveField(Fields.TOKEN0)
          }}
        >
          {currency0 ? (
            <Row>
              <CurrencyLogo currency={currency0} />
              <Text fontWeight={500} fontSize={20} marginLeft={'12px'}>
                {currency0.symbol}
              </Text>
            </Row>
          ) : (
            <Text fontWeight={500} fontSize={20} marginLeft={'12px'}>
              {t('selectAToken')}
            </Text>
          )}
        </ButtonDropdownLightNew>

        <ColumnCenter>
          <PlusIcon><Plus size="16" color={themes.text2} /></PlusIcon>
          {/* <Plus size="16" color="#888D9B" /> */}
        </ColumnCenter>

        <ButtonDropdownLightNew
          onClick={() => {
            setShowSearch(true)
            setActiveField(Fields.TOKEN1)
          }}
        >
          {currency1 ? (
            <Row>
              <CurrencyLogo currency={currency1} />
              <Text fontWeight={500} fontSize={20} marginLeft={'12px'}>
                {currency1.symbol}
              </Text>
            </Row>
          ) : (
            <Text fontWeight={500} fontSize={20} marginLeft={'12px'}>
              {t('selectAToken')}
            </Text>
          )}
        </ButtonDropdownLightNew>

        {
          hasPosition && (
            <ColumnCenter
              style={{ justifyItems: 'center', backgroundColor: '', padding: '12px 0px', borderRadius: '12px' }}
            >
              <Text textAlign="center" fontWeight={500}>
                {t('Pool Found')}!
              </Text>
              <StyledInternalLink to={`/pool`}>
                <Text textAlign="center">{t('Manage this pool')}.</Text>
              </StyledInternalLink>
            </ColumnCenter>
          )
        }

        {
          currency0 && currency1 ? (
            pairState === PairState.EXISTS ? (
              hasPosition && pair ? (
                ''
              ) : (
                <LightCardNew padding="45px 10px">
                  <AutoColumn gap="sm" justify="center">
                    <Text textAlign="center">You don’t have liquidity in this pool yet.</Text>
                    <StyledInternalNewLink to={`/add/${currencyId(currency0)}/${currencyId(currency1)}`}>
                      <Text textAlign="center">{t('Add Liquidity')}.</Text>
                    </StyledInternalNewLink>

                  </AutoColumn>
                </LightCardNew>
              )
            ) : validPairNoLiquidity ? (
              <LightCardNew padding="45px 10px">
                <AutoColumn gap="sm" justify="center">
                  <Text textAlign="center">No pool found.</Text>
                  <StyledInternalNewLink to={`/add/${currencyId(currency0)}/${currencyId(currency1)}`}>
                    Create pool.
                  </StyledInternalNewLink>
                </AutoColumn>
              </LightCardNew>
            ) : pairState === PairState.INVALID ? (
              <LightCardNew padding="45px 10px">
                <AutoColumn gap="sm" justify="center">
                  <Text textAlign="center" fontWeight={500}>
                    {t('Invalid pair')}
                  </Text>
                </AutoColumn>
              </LightCardNew>
            ) : pairState === PairState.LOADING ? (
              <LightCardNew padding="45px 10px">
                <AutoColumn gap="sm" justify="center">
                  <Text textAlign="center">
                    {t('Loading')}
                    <Dots />
                  </Text>
                </AutoColumn>
              </LightCardNew>
            ) : null
          ) : (
            prerequisiteMessage
          )
        }
      </AutoColumn >

      <CurrencySearchModal
        isOpen={showSearch}
        onCurrencySelect={handleCurrencySelect}
        onDismiss={handleSearchDismiss}
        showCommonBases
        selectedCurrency={(activeField === Fields.TOKEN0 ? currency1 : currency0) ?? undefined}
      />

    </AppBody >
    {
      currency0 && currency1 ? (
        pairState === PairState.EXISTS ? (
          (
            hasPosition && pair ? (
              <div style={{ marginTop: '1rem', width: '100%',maxWidth:'420px' }}>
                <MinimalPositionCard pair={pair} />
              </div>
            ) : (''))
        ) : ''
      ) : ''
    }
  </>
  )
}
